/**
 * Local Delivery Cart checkout flow controller.
 * Checkout-safe attribution fire-and-forget: 2026-05-26.
 */
(function () {
  'use strict';

  function createCheckoutFlowController(ctx) {
    ctx = ctx || {};

    function isRetiredAttributionAttribute(key) {
      return key === 'organic_referrer' || key === 'campaign_id' || key.indexOf('utm_') === 0 || key.indexOf('wf_attr_') === 0;
    }

    // Order-stitch bridge: Shopify's order webhook cart_token no longer matches
    // the /cart.js token, so the worker stitches by this session id instead.
    // Written only here, inside the single merged delivery cart write - the
    // standalone tracker never writes cart attributes.
    function readAttributionSessionId() {
      try {
        var storage = (typeof sessionStorage !== 'undefined' && sessionStorage) || window.sessionStorage;
        if (!storage || typeof storage.getItem !== 'function') return null;
        var raw = storage.getItem('wf_attr_session_v1');
        if (!raw) return null;
        var session = JSON.parse(raw);
        if (!session || typeof session !== 'object') return null;
        if (typeof session.expiresAt === 'number' && session.expiresAt < Date.now()) return null;
        var sid = typeof session.sessionId === 'string' ? session.sessionId : '';
        return /^[A-Za-z0-9:_-]{1,128}$/.test(sid) ? sid : null;
      } catch (_err) {
        return null;
      }
    }

    function getFetch() {
      return (ctx.deps && typeof ctx.deps.fetch === 'function') ? ctx.deps.fetch : fetch;
    }

    function getEl(id) {
      return ctx.dom && typeof ctx.dom.getEl === 'function' ? ctx.dom.getEl(id) : null;
    }

    function getCartNoteValue() {
      var txt = document.getElementById('aef-note');
      if (txt && typeof txt.value === 'string') return txt.value;
      var hidden = document.getElementById('aef-note-hidden');
      if (hidden && typeof hidden.value === 'string') return hidden.value;
      var form = document.getElementById('cart_form');
      if (!form) return '';
      if (form.elements && typeof form.elements.namedItem === 'function') {
        var associatedNote = form.elements.namedItem('note');
        if (associatedNote && typeof associatedNote.value === 'string') return associatedNote.value;
      }
      var noteInput = form.querySelector('textarea[name="note"], input[name="note"]');
      if (noteInput && typeof noteInput.value === 'string') return noteInput.value;
      if (typeof document.querySelector !== 'function') return '';
      var detachedNoteInput = document.querySelector('textarea[name="note"][form="cart_form"], input[name="note"][form="cart_form"]');
      return detachedNoteInput && typeof detachedNoteInput.value === 'string' ? detachedNoteInput.value : '';
    }

    function showCheckoutSyncError(btn, err) {
      if (window.WF_LD_UPSELL) {
        if (typeof window.WF_LD_UPSELL.resetCheckoutTransitionState === 'function') {
          window.WF_LD_UPSELL.resetCheckoutTransitionState();
        }
        if (typeof window.WF_LD_UPSELL.hide === 'function') {
          window.WF_LD_UPSELL.hide();
        }
      }
      var hint = getEl('wf-ld-checkout-hint');
      if (hint) {
        hint.textContent = 'We could not sync delivery details. Please try again.';
        hint.style.display = 'block';
      }
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Checkout';
        if (typeof btn.removeAttribute === 'function') btn.removeAttribute('aria-busy');
      }
      console.error('[wf-ld] blocking checkout due to attribute sync failure', err);
    }

    function showCheckoutPendingState(btn, message) {
      var pendingMessage = message || 'Preparing your secure checkout...';
      var hint = getEl('wf-ld-checkout-hint');
      if (hint) {
        hint.textContent = pendingMessage;
        hint.style.display = 'block';
      }
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        if (typeof btn.setAttribute === 'function') btn.setAttribute('aria-busy', 'true');
      }
      if (window.WF_LD_UPSELL && typeof window.WF_LD_UPSELL.setCheckoutTransitionState === 'function') {
        window.WF_LD_UPSELL.setCheckoutTransitionState(pendingMessage);
      }
    }

    function verifyRequiredCartAttributes(deliveryMethod, deliveryDate, deliveryTimeslot) {
      return getFetch()('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cart) {
          var attrs = cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
          var methodVal = String(attrs['Delivery Method'] || '').trim();
          if (methodVal.toLowerCase() !== String(deliveryMethod || '').toLowerCase()) {
            throw new Error('Delivery Method not synced');
          }
          if (String(deliveryMethod || '').toLowerCase() === 'collection') {
            if (String(attrs.collection_date || '').trim() !== String(deliveryDate || '').trim()) {
              throw new Error('collection_date not synced');
            }
            if (String(attrs.collection_timeslot || '').trim() !== String(deliveryTimeslot || '').trim()) {
              throw new Error('collection_timeslot not synced');
            }
            return;
          }
          if (String(attrs.delivery_date || '').trim() !== String(deliveryDate || '').trim()) {
            throw new Error('delivery_date not synced');
          }
          if (String(attrs.delivery_timeslot || '').trim() !== String(deliveryTimeslot || '').trim()) {
            throw new Error('delivery_timeslot not synced');
          }
        });
    }

    function readCartAttributes() {
      return getFetch()('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cart) {
          return cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
        })
        .catch(function () {
          return {};
        });
    }

    function readCartFormAttributes() {
      var form = document.getElementById('cart_form');
      if (!form) return {};

      var attrs = {};
      var fields = form.elements || (typeof form.querySelectorAll === 'function'
        ? form.querySelectorAll('[name^="attributes["][name$="]"]')
        : []);
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        if (!field || typeof field.name !== 'string') continue;
        var name = field.name;
        if (name.indexOf('attributes[') !== 0 || name.charAt(name.length - 1) !== ']') continue;

        var key = name.substring(11, name.length - 1);
        if (!key) continue;
        if (isRetiredAttributionAttribute(key)) continue;

        var type = String(field.type || '').toLowerCase();
        if ((type === 'radio' || type === 'checkbox') && !field.checked) continue;

        var value = field.value;
        if (value === null || value === undefined) continue;
        if (String(value).trim() === '') continue;
        attrs[key] = String(value).slice(0, 255);
      }

      return attrs;
    }

    function syncAttributionTrackerIfPresent() {
      if (!window.__wfAttributionTracker || typeof window.__wfAttributionTracker.sync !== 'function') {
        return Promise.resolve();
      }

      try {
        var result = window.__wfAttributionTracker.sync();
        if (result && typeof result.then === 'function') {
          result.catch(function () {});
        }
      } catch (_err) {}
      return Promise.resolve();
    }

    function mergeCartAttributes(existingAttributes, formAttributes, nextAttributes) {
      var merged = {};
      var existing = existingAttributes && typeof existingAttributes === 'object' ? existingAttributes : {};
      var form = formAttributes && typeof formAttributes === 'object' ? formAttributes : {};
      var next = nextAttributes && typeof nextAttributes === 'object' ? nextAttributes : {};

      Object.keys(existing).forEach(function (key) {
        if (isRetiredAttributionAttribute(key)) return;
        var value = existing[key];
        if (value === null || value === undefined) return;
        merged[key] = String(value).slice(0, 255);
      });

      Object.keys(form).forEach(function (key) {
        if (isRetiredAttributionAttribute(key)) return;
        var value = form[key];
        if (value === null || value === undefined) return;
        if (String(value).trim() === '') return;
        merged[key] = String(value).slice(0, 255);
      });

      Object.keys(next).forEach(function (key) {
        if (isRetiredAttributionAttribute(key)) return;
        var value = next[key];
        if (value === null || value === undefined) {
          delete merged[key];
          return;
        }
        merged[key] = String(value).slice(0, 255);
      });

      return merged;
    }

    function updateCartNoteAndAttributes(cartAttributes, customerNote) {
      return syncAttributionTrackerIfPresent()
        .then(function () {
          return readCartAttributes();
        })
        .then(function (existingAttributes) {
          var formAttributes = readCartFormAttributes();
          var mergedAttributes = mergeCartAttributes(existingAttributes, formAttributes, cartAttributes);
          var attributionSessionId = readAttributionSessionId();
          if (attributionSessionId) {
            mergedAttributes.wf_attr_session_id = attributionSessionId;
          }
          return getFetch()('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              attributes: mergedAttributes,
              note: customerNote,
            }),
          });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('Cart update failed: ' + r.status);
          return r.json();
        });
    }

    function proceedToCheckout(btn, seasonalChecked) {
      var slotLbl = ctx.getters.getActiveTimeslotValue();
      var slotId = ctx.getters.getActiveTimeslotId();
      var isExp = ctx.getters.getActiveTimeslotType() === 'express';
      var expressSelectionError = ctx.getters.getExpressSelectionError();
      if (expressSelectionError) {
        var pricingHint = getEl('wf-ld-checkout-hint');
        if (pricingHint) {
          pricingHint.textContent = expressSelectionError;
          pricingHint.style.display = 'block';
        }
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Checkout';
        }
        return;
      }

      showCheckoutPendingState(btn);

      var methodD = getEl('wf-ld-method-delivery');
      var deliveryMethod = isExp ? 'Express' : ((methodD && methodD.checked) ? 'Delivery' : 'Collection');
      var rawDate = ctx.getters.getSelectedDateIso();

      if (!seasonalChecked) {
        ctx.actions.runSeasonalConflictCheck(rawDate, getEl('wf-ld-seasonal-hint')).then(function (conflicts) {
          if (conflicts && conflicts.length > 0) {
            var seasonalBlockHint = getEl('wf-ld-checkout-hint');
            if (seasonalBlockHint) {
              seasonalBlockHint.textContent = conflicts.join(' ');
              seasonalBlockHint.style.display = 'block';
            }
            if (btn) {
              btn.disabled = false;
              btn.textContent = 'Checkout';
            }
            return;
          }
          proceedToCheckout(btn, true);
        });
        return;
      }

      ctx.actions.populateTimeslots(rawDate);
      if (ctx.getters.isActiveSlotPastCutoff && ctx.getters.isActiveSlotPastCutoff(rawDate)) {
        var cutoffHint = getEl('wf-ld-checkout-hint');
        if (cutoffHint) {
          cutoffHint.textContent = 'Selected timeslot cutoff has passed. Please choose another timeslot.';
          cutoffHint.style.display = 'block';
        }
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Checkout';
        }
        ctx.actions.validateAndBlockCheckout();
        return;
      }
      if (!ctx.actions.isDateAllowed(rawDate) || !ctx.actions.isSelectedTimeslotValid()) {
        var hint = getEl('wf-ld-checkout-hint');
        if (hint) {
          hint.textContent = 'Selected date/timeslot is no longer available. Please reselect using current availability.';
          hint.style.display = 'block';
        }
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Checkout';
        }
        ctx.actions.validateAndBlockCheckout();
        return;
      }

      if (
        ctx.deps &&
        ctx.deps.API &&
        typeof ctx.deps.API.reportCartFormInteraction === 'function'
      ) {
        ctx.deps.API.reportCartFormInteraction('checkout_started', {
          deliveryMethod: deliveryMethod,
          senderEmail: ctx.getters.getFormVal('sender_email'),
        });
      }

      var deliveryDate = ctx.deps.CHECKOUT.isoToDmy(rawDate);
      var deliveryTimeslot = slotLbl;
      var timeVal = deliveryDate && deliveryTimeslot ? (deliveryDate + ' ' + deliveryTimeslot) : '';

      var rFirst = ctx.getters.getFormVal('recipient_first_name');
      var rLast = ctx.getters.getFormVal('recipient_last_name');
      var rPhone = ctx.getters.getFormVal('recipient_phone');
      var rAddr1 = ctx.getters.getFormVal('recipient_address1');
      var rAddr2 = ctx.getters.getFormVal('recipient_address2');
      var rPostal = ctx.getters.getFormVal('recipient_postal');
      var sFirst = ctx.getters.getFormVal('sender_first_name');
      var sLast = ctx.getters.getFormVal('sender_last_name');
      var sEmail = ctx.getters.getFormVal('sender_email');
      var sPhone = ctx.getters.getFormVal('sender_phone');
      var customerNote = getCartNoteValue();

      if (deliveryMethod === 'Collection') {
        var resolvedRecipient = ctx.actions.applyCollectionRecipientFallback({
          firstName: rFirst,
          lastName: rLast,
          phone: rPhone,
          address1: rAddr1,
          address2: rAddr2,
          postal: rPostal,
        }, {
          firstName: sFirst,
          lastName: sLast,
          phone: sPhone,
        });
        rFirst = resolvedRecipient.firstName || '';
        rLast = resolvedRecipient.lastName || '';
        rPhone = resolvedRecipient.phone || '';
        rAddr1 = resolvedRecipient.address1 || '';
        rAddr2 = resolvedRecipient.address2 || '';
        rPostal = resolvedRecipient.postal || '';
      }

      var anonCb = getEl('wf-ld-anonymous');
      var ANON_TAG = '⚠️ SENDER WISHES TO REMAIN ANONYMOUS — Do not reveal sender identity to recipient.';
      if (anonCb && anonCb.checked) {
        if (customerNote.indexOf(ANON_TAG) === -1) {
          customerNote = ANON_TAG + (customerNote ? '\n\n' + customerNote : '');
        }
      }

      var cartAttributes = ctx.deps.CHECKOUT.buildCartAttributes({
        deliveryMethod: deliveryMethod,
        deliveryDate: deliveryDate,
        deliveryTimeslot: deliveryTimeslot,
        slotId: slotId,
        timeVal: timeVal,
        anonChecked: !!(anonCb && anonCb.checked),
        recipient: {
          firstName: rFirst,
          lastName: rLast,
          phone: rPhone,
          address1: rAddr1,
          address2: rAddr2,
          postal: rPostal,
        },
        sender: {
          firstName: sFirst,
          lastName: sLast,
          email: sEmail,
          phone: sPhone,
        },
      });

      if (deliveryMethod === 'Collection') {
        console.log('[wf-ld] Collection → Storefront API (Pick up pre-select)');
        var clearCollectionFeePromise = ctx.deps.EXPRESS.removeAllFeeProducts
          ? ctx.deps.EXPRESS.removeAllFeeProducts()
          : (ctx.deps.EXPRESS.removeAllExpressProducts ? ctx.deps.EXPRESS.removeAllExpressProducts() : Promise.resolve());
        clearCollectionFeePromise.then(function () {
          ctx.deps.EXPRESS.refreshCartTotals();
          if (ctx.deps.EXPRESS.setFeeChargeVisible) ctx.deps.EXPRESS.setFeeChargeVisible(false);
          else if (ctx.deps.EXPRESS.setExpressChargeVisible) ctx.deps.EXPRESS.setExpressChargeVisible(false);
          ctx.actions.setDeliveryFeeRow('Delivery Fee', null);
          return updateCartNoteAndAttributes(cartAttributes, customerNote);
          })
          .then(function () {
            return verifyRequiredCartAttributes(deliveryMethod, deliveryDate, deliveryTimeslot);
          })
          .then(function () {
            return getFetch()('/cart.js', { credentials: 'same-origin' });
          })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var items = (cart.items || []).map(function (it) {
              var entry = { variantId: String(it.variant_id), quantity: it.quantity };
              if (it.properties && Object.keys(it.properties).length > 0) {
                entry.properties = Object.keys(it.properties).map(function (k) {
                  return { name: k, value: String(it.properties[k]) };
                });
              }
              return entry;
            });
            var attributionSessionId = readAttributionSessionId();
            return ctx.deps.API.createCheckout(
              ctx.deps.CHECKOUT.buildStorefrontCheckoutPayload({
                customAttributes: attributionSessionId
                  ? [{ key: 'wf_attr_session_id', value: attributionSessionId }]
                  : [],
                lineItems: items,
                deliveryMethod: deliveryMethod,
                deliveryDate: deliveryDate,
                deliveryDateIso: rawDate,
                deliveryTimeslot: deliveryTimeslot,
                deliverySlotId: slotId,
                recipient: {
                  firstName: rFirst,
                  lastName: rLast,
                  phone: rPhone,
                  address1: rAddr1,
                  address2: rAddr2,
                  postal: rPostal,
                },
                sender: {
                  firstName: sFirst,
                  lastName: sLast,
                  email: sEmail,
                  phone: sPhone,
                },
                note: customerNote,
              })
            );
          })
          .then(function (data) {
            if (!data || !data.checkoutUrl) throw new Error('No checkout URL returned');
            console.log('[wf-ld] redirecting to Storefront checkout (Pick up)');
            ctx.deps.STORAGE.clear();
            window.location.href = data.checkoutUrl;
          })
          .catch(function (err) {
            console.error('[wf-ld] Collection checkout error, falling back to native checkout', err);
            var fallbackPrefill = {
              sEmail: sEmail,
              sFirst: sFirst,
              sLast: sLast,
              sPhone: sPhone,
              rFirst: rFirst,
              rLast: rLast,
              rPhone: rPhone,
              rAddr1: rAddr1,
              rAddr2: rAddr2,
              rPostal: rPostal,
            };
            var fallbackUrl = '';
            if (ctx.deps.CHECKOUT && typeof ctx.deps.CHECKOUT.fallbackToNativeCheckout === 'function') {
              fallbackUrl = ctx.deps.CHECKOUT.fallbackToNativeCheckout(fallbackPrefill) || '';
            } else if (ctx.deps.CHECKOUT && typeof ctx.deps.CHECKOUT.buildNativeCheckoutUrl === 'function') {
              fallbackUrl = ctx.deps.CHECKOUT.buildNativeCheckoutUrl(fallbackPrefill) || '';
            }
            if (!fallbackUrl) {
              showCheckoutSyncError(btn, err);
              return;
            }
            ctx.deps.STORAGE.clear();
            window.location.href = fallbackUrl;
          });
      } else {
        console.log('[wf-ld] Delivery/Express → AJAX Cart (source_name: web)');
        updateCartNoteAndAttributes(cartAttributes, customerNote)
          .then(function () {
            return verifyRequiredCartAttributes(deliveryMethod, deliveryDate, deliveryTimeslot);
          })
          .then(function () {
            var checkoutUrl = ctx.deps.CHECKOUT.buildNativeCheckoutUrl({
              sEmail: sEmail,
              sFirst: sFirst,
              sLast: sLast,
              sPhone: sPhone,
              rFirst: rFirst,
              rLast: rLast,
              rPhone: rPhone,
              rAddr1: rAddr1,
              rAddr2: rAddr2,
              rPostal: rPostal,
            });
            console.log('[wf-ld] redirecting to native checkout');
            ctx.deps.STORAGE.clear();
            window.location.href = checkoutUrl;
          })
          .catch(function (err) {
            showCheckoutSyncError(btn, err);
          });
      }
    }

    return {
      getCartNoteValue: getCartNoteValue,
      proceedToCheckout: proceedToCheckout,
    };
  }

  window.WF_LD_CART_CHECKOUT_FLOW = {
    createCheckoutFlowController: createCheckoutFlowController,
  };
})();
